import React, { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { readTimer, timerKey, writeTimer } from "../utils/timerStorage";

const QUESTION_TYPE_THRESHOLD = 2;

export const CircularTimer = ({ questionIndex, title, type, onTimerEnd }) => {
  const initialTimeInSeconds =
    useSelector((state) => state.assessment.initialTimes[questionIndex]) || 0;
  const assessmentId = useSelector((state) => state.assessment.id);
  const userId = useSelector((state) => state.assessment.userID);

  const storageKey = timerKey(assessmentId, userId, questionIndex);

  const [seconds, setSeconds] = React.useState(() => {
    const saved = readTimer(storageKey);
    return saved !== null ? saved : initialTimeInSeconds;
  });
  // onTimerEnd is written inline by the caller, so its identity changes on every
  // render. Holding it in a ref keeps the interval below from being torn down and
  // restarted a second at a time.
  const onTimerEndRef = useRef(onTimerEnd);
  useEffect(() => {
    onTimerEndRef.current = onTimerEnd;
  }, [onTimerEnd]);

  // Whether this clock has already announced that it reached zero. Without it the
  // total-time clock called handleSubmit once a second for the rest of the sitting.
  const hasEndedRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds((prev) => {
        // CR-03: this used to stop dead at zero. It now keeps counting, into negative
        // numbers, which is what the overtime display below reads - a student who runs
        // over is not thrown out of the question, only told that they have run over.
        const next = prev - 1;
        writeTimer(storageKey, next);
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [storageKey]);

  // Announce zero exactly once. The total-time clock uses this to auto-submit; the
  // per-question clock passes no handler, so running over simply keeps counting.
  useEffect(() => {
    if (seconds <= 0 && !hasEndedRef.current) {
      hasEndedRef.current = true;
      onTimerEndRef.current && onTimerEndRef.current();
    }
  }, [seconds]);

  useEffect(() => {
    const saved = readTimer(storageKey);
    // Deliberately armed rather than suppressed: if the student reloads after the total
    // time has already run out, the paper is over and the auto-submit has to fire again.
    // The effect above still limits it to once per clock.
    hasEndedRef.current = false;
    setSeconds(saved !== null ? saved : initialTimeInSeconds);
  }, [storageKey, initialTimeInSeconds]);

  const isOvertime = seconds < 0;

  const getColorBasedOnTime = useCallback(() => {
    if (isOvertime) {
      // Overtime is not a failure, so it is amber rather than the red the last seconds
      // of the countdown already use.
      return {
        progressColor: "text-amber-500",
        pathColor: "text-amber-100",
      };
    }

    const percentageTimeLeft = initialTimeInSeconds
      ? (seconds / initialTimeInSeconds) * 100
      : 0;

    if (percentageTimeLeft > 50) {
      return {
        progressColor:
          type === QUESTION_TYPE_THRESHOLD ? "text-green-300" : "text-blue-300",
        pathColor:
          type === QUESTION_TYPE_THRESHOLD ? "text-green-100" : "text-blue-100",
      };
    } else if (percentageTimeLeft > 20) {
      return {
        progressColor: "text-yellow-300",
        pathColor: "text-yellow-100",
      };
    } else if (percentageTimeLeft > 10) {
      return {
        progressColor: "text-orange-300",
        pathColor: "text-orange-100",
      };
    }
    return {
      progressColor: "text-red-300",
      pathColor: "text-red-100",
    };
  }, [isOvertime, seconds, initialTimeInSeconds, type]);

  const { progressColor, pathColor } = getColorBasedOnTime();

  // Clamped, so overtime does not feed a negative length into the arc - SVG draws that
  // as a full ring, which reads as the clock having reset.
  const circumference = 2 * Math.PI * 45;
  const remainingRatio = initialTimeInSeconds
    ? Math.min(Math.max(seconds / initialTimeInSeconds, 0), 1)
    : 0;

  const displaySeconds = Math.abs(seconds);
  const clock = `${Math.floor(displaySeconds / 60)
    .toString()
    .padStart(2, "0")}:${(displaySeconds % 60).toString().padStart(2, "0")}`;

  return (
    <div className="flex flex-col items-center justify-center lg:h-full w-[calc(50%_-_16px)] lg:w-full p-4 bg-white rounded-lg shadow-md gap-3 lg:min-h-[300px]">
      <svg
        className={`lg:w-24 lg:h-24 w-16 h-16 transform rotate-90 ${progressColor}`}
        viewBox="0 0 100 100"
      >
        <circle
          className={`stroke-current ${pathColor}`}
          fill="none"
          strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset="0"
          cx="50"
          cy="50"
          r="45"
        />
        <circle
          className="stroke-current"
          fill="none"
          strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={remainingRatio * circumference}
          cx="50"
          cy="50"
          r="45"
        />
      </svg>
      <div className="text-gray-800 text-center ">
        <h1
          className={`texzt-xl lg:text-2xl font-bold mb-2 ${
            isOvertime ? "text-amber-600" : ""
          }`}
        >
          {isOvertime ? `+${clock}` : clock}
        </h1>
        <h2 className="text-lg lg:text-xl">{title} </h2>
      </div>
    </div>
  );
};

export default CircularTimer;
